import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
  type ReactNode,
} from "react";

import type { DesktopSnapshot, ExportCsvInput, ExportCsvResult } from "@zentrack/desktop-api";
import { createWebDesktopApi, type WebDesktopApi } from "@zentrack/web-api";

interface WebAppContextValue {
  api: WebDesktopApi;
  errorMessage: string | null;
  exportReportsCsv(input?: ExportCsvInput): Promise<ExportCsvResult | null>;
  isLoading: boolean;
  perform<T>(operation: () => Promise<T>): Promise<T>;
  refresh(): Promise<void>;
  setErrorMessage(value: string | null): void;
  snapshot: DesktopSnapshot | null;
}

const WebAppContext = createContext<WebAppContextValue | null>(null);

function extractMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

/**
 * Module-level singleton promise for the IndexedDB-backed runtime. Shared
 * between WebAppProvider and the TanStack Router index-route redirect so the
 * router can read the persisted last-route setting in beforeLoad without going
 * through React context.
 */
let webApiPromise: Promise<WebDesktopApi> | null = null;

export function getWebApi(): Promise<WebDesktopApi> {
  if (!webApiPromise) {
    webApiPromise = createWebDesktopApi();
  }
  return webApiPromise;
}

export function WebAppProvider({ children }: { children: ReactNode }) {
  const [api, setApi] = useState<WebDesktopApi | null>(null);
  const [snapshot, setSnapshot] = useState<DesktopSnapshot | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve the shared IndexedDB-backed runtime once. getWebApi() caches the
  // promise so the router's beforeLoad and this provider use the same instance.
  useEffect(() => {
    let cancelled = false;
    getWebApi()
      .then((instance) => {
        if (cancelled) {
          return;
        }
        setApi(instance);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setErrorMessage(extractMessage(error));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!api) {
      return;
    }

    try {
      const nextSnapshot = await api.bootstrap();
      setErrorMessage(null);
      startTransition(() => {
        setSnapshot(nextSnapshot);
        setIsLoading(false);
      });
    } catch (error) {
      setErrorMessage(extractMessage(error));
      setIsLoading(false);
    }
  }, [api]);

  // Bootstrap as soon as the api instance is ready.
  useEffect(() => {
    if (api) {
      void refresh();
    }
  }, [api, refresh]);

  const perform = useCallback(
    async <T,>(operation: () => Promise<T>): Promise<T> => {
      try {
        const result = await operation();
        await refresh();
        return result;
      } catch (error) {
        const message = extractMessage(error);
        setErrorMessage(message);
        throw error;
      }
    },
    [refresh],
  );

  const exportReportsCsv = useCallback(
    async (input?: ExportCsvInput): Promise<ExportCsvResult | null> => {
      if (!api) {
        return null;
      }
      setErrorMessage(null);
      return perform(() => api.reports.exportCsv(input));
    },
    [api, perform],
  );

  // ---------------------------------------------------------------------------
  // Active timer polling — refresh the live timer display while a session runs.
  // ---------------------------------------------------------------------------

  const refreshActiveTimer = useCallback(async () => {
    if (!api) {
      return;
    }

    try {
      const [activeSession, tray] = await Promise.all([
        api.timer.getCurrentSession(),
        api.tray.getState(),
      ]);
      setErrorMessage(null);
      startTransition(() => {
        setSnapshot((current) =>
          current ? { ...current, activeSession, tray } : current,
        );
        setIsLoading(false);
      });
    } catch (error) {
      setErrorMessage(extractMessage(error));
    }
  }, [api]);

  useEffect(() => {
    if (!api || !snapshot?.activeSession || snapshot.activeSession.pausedAt) {
      return;
    }

    const interval = window.setInterval(() => {
      void refreshActiveTimer();
    }, 2000);

    return () => window.clearInterval(interval);
  }, [api, refreshActiveTimer, snapshot?.activeSession, snapshot?.activeSession?.pausedAt]);

  // ---------------------------------------------------------------------------
  // Browser idle detection (DOM-event based). Honest best-effort pause.
  // ---------------------------------------------------------------------------

  const lastActivityRef = useRef<number>(Date.now());
  const hasAutoPausedThisIdleRef = useRef<boolean>(false);

  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener("mousemove", updateActivity, { passive: true });
    window.addEventListener("keydown", updateActivity, { passive: true });
    window.addEventListener("pointerdown", updateActivity, { passive: true });
    window.addEventListener("scroll", updateActivity, { passive: true });

    return () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("pointerdown", updateActivity);
      window.removeEventListener("scroll", updateActivity);
    };
  }, []);

  useEffect(() => {
    if (!api) {
      return;
    }

    const settings = snapshot?.settings;
    const activeSession = snapshot?.activeSession;

    if (!settings?.autoPauseOnIdle || !activeSession || activeSession.pausedAt) {
      return;
    }

    const thresholdMs = settings.idleThresholdMinutes * 60 * 1000;

    const interval = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;

      if (idleMs < 5_000) {
        hasAutoPausedThisIdleRef.current = false;
      }

      if (idleMs >= thresholdMs && !hasAutoPausedThisIdleRef.current) {
        hasAutoPausedThisIdleRef.current = true;
        void perform(() => api.timer.pause()).catch(() => {});
      }
    }, 10_000);

    return () => window.clearInterval(interval);
  }, [
    api,
    perform,
    snapshot?.activeSession,
    snapshot?.activeSession?.pausedAt,
    snapshot?.settings?.autoPauseOnIdle,
    snapshot?.settings?.idleThresholdMinutes,
  ]);

  const value = useMemo<WebAppContextValue | null>(() => {
    if (!api) {
      return null;
    }

    return {
      api,
      errorMessage,
      exportReportsCsv,
      isLoading,
      perform,
      refresh,
      setErrorMessage,
      snapshot,
    };
  }, [api, errorMessage, exportReportsCsv, isLoading, perform, refresh, snapshot]);

  if (!value) {
    return null;
  }

  return <WebAppContext.Provider value={value}>{children}</WebAppContext.Provider>;
}

export function useWebApp(): WebAppContextValue {
  const context = useContext(WebAppContext);

  if (!context) {
    throw new Error("useWebApp must be used inside WebAppProvider.");
  }

  return context;
}
